import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUpRight, Mail, Clock, CheckCircle2 } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import { PHOTOS, STAN_STRATEGY_URL } from '@/constants/content';
import { CONTACT } from '@/constants/testIds';
import { submitContact } from '@/lib/api';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

const KINDS = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'speaking', label: 'Speaking Request' },
  { value: 'media', label: 'Media Request' },
  { value: 'partnership', label: 'Partnership Opportunity' },
  { value: 'business', label: 'Business Question' },
];

export default function Contact() {
  usePageMeta({ ...SEO.contact });
  const search = useLocation().search;
  const initialKind = useMemo(() => {
    const params = new URLSearchParams(search);
    const k = params.get('kind');
    return KINDS.find((x) => x.value === k)?.value || 'general';
  }, [search]);

  const [form, setForm] = useState({
    kind: initialKind, name: '', email: '', organization: '', subject: '', message: '',
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(false), 60000);
    return () => clearTimeout(t);
  }, [sent]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      setForm({ kind: 'general', name: '', email: '', organization: '', subject: '', message: '' });
    } catch (err) {
      // Sonner not used to keep the elegant confirmation inline
      setSent(false);
      alert('Could not submit. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7 min-w-0">
            <div className="eyebrow text-taupe mb-8">Contact · The Desk</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              Say hello.
              <br />
              <em className="italic text-taupe">Start a conversation.</em>
            </h1>
          </div>
          <div className="lg:col-span-5 min-w-0">
            <p className="text-matte/70 leading-[1.85] text-[15px] max-w-md">
              For speaking, media, partnerships, and general inquiries, write below and the studio
              will respond within 1–2 business days.
            </p>
          </div>
        </div>
      </section>

      {/* TWO-COLUMN LAYOUT */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT: Contact info + photo */}
          <aside className="lg:col-span-5 lg:sticky lg:top-32 self-start space-y-10 min-w-0">
            <div className="rounded-3xl overflow-hidden luxury-shadow">
              <img
                src={PHOTOS.contact}
                alt="Toia Wallace at her Altura Global RE office"
                className="w-full h-[52vh] object-cover"
                loading="lazy"
              />
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-10 luxury-shadow">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-full bg-ivory border border-taupe/30 grid place-items-center flex-shrink-0">
                  <Mail size={16} strokeWidth={1.4} className="text-matte" />
                </span>
                <div>
                  <div className="eyebrow text-taupe mb-1">Support Email</div>
                  <a href="mailto:iamtoiawallace@gmail.com" className="font-serif-display text-2xl text-matte hover:text-gold transition-colors" data-testid="contact-support-email">
                    iamtoiawallace@gmail.com
                  </a>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-taupe/20 flex items-start gap-4">
                <span className="w-10 h-10 rounded-full bg-ivory border border-taupe/30 grid place-items-center flex-shrink-0">
                  <Clock size={16} strokeWidth={1.4} className="text-matte" />
                </span>
                <div>
                  <div className="eyebrow text-taupe mb-2">Business Hours</div>
                  <div className="font-serif-display text-xl text-matte tracking-tight">Monday – Friday</div>
                  <div className="mt-2 space-y-1 text-sm text-matte/70">
                    <div>8:00 AM – 4:00 PM CST</div>
                    <div>9:00 AM – 5:00 PM EST</div>
                  </div>
                  <p className="mt-4 text-xs text-matte/60 leading-relaxed max-w-xs">
                    We strive to respond to all inquiries within 1–2 business days.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* RIGHT: form + Strategy Intensive */}
          <div className="lg:col-span-7 space-y-16 min-w-0">
            {/* Form */}
            <div>
              <SectionMarker number="01" label="Send a Message" />
              <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-matte">
                Write to the studio.
              </h2>

              {sent ? (
                <ConfirmationCard onWriteAnother={() => setSent(false)} />
              ) : (
                <form onSubmit={submit} data-testid={CONTACT.form} className="mt-12 grid sm:grid-cols-2 gap-x-8 gap-y-2">
                  <Field label="Full Name" required value={form.name} onChange={set('name')} testId={CONTACT.name} />
                  <Field label="Email Address" type="email" required value={form.email} onChange={set('email')} testId={CONTACT.email} />
                  <Field label="Company (Optional)" value={form.organization} onChange={set('organization')} testId={CONTACT.org} />
                  <div>
                    <label className="block eyebrow text-taupe mb-3 mt-6">Inquiry Type</label>
                    <select
                      value={form.kind}
                      onChange={set('kind')}
                      data-testid={CONTACT.kind}
                      className="w-full bg-transparent border-b border-taupe/40 py-3 text-matte focus:outline-none focus:border-gold font-serif-display text-xl"
                    >
                      {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Subject" value={form.subject} onChange={set('subject')} testId={CONTACT.subject} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block eyebrow text-taupe mb-3 mt-6">Message</label>
                    <textarea
                      rows={6}
                      required
                      value={form.message}
                      onChange={set('message')}
                      data-testid={CONTACT.message}
                      className="w-full bg-transparent border-b border-taupe/40 py-3 text-matte focus:outline-none focus:border-gold resize-none"
                    />
                  </div>
                  <div className="sm:col-span-2 mt-10">
                    <button type="submit" disabled={loading} data-testid={CONTACT.submit} className="btn-primary disabled:opacity-50">
                      {loading ? 'Sending…' : 'Send Message'}
                      <ArrowUpRight size={14} strokeWidth={1.6} />
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Strategy Intensive */}
            <div className="rounded-3xl bg-matte text-ivory p-10 md:p-14 luxury-shadow relative overflow-hidden">
              <div className="absolute inset-0 opacity-25 noise-grain pointer-events-none" aria-hidden />
              <div className="relative">
                <div className="eyebrow text-gold mb-8">Need Strategic Guidance?</div>
                <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
                  Book a <em className="italic text-gold">Strategy Intensive.</em>
                </h3>
                <div className="mt-10 space-y-6 text-ivory/75 leading-[1.85] max-w-2xl">
                  <p>
                    Work directly with Toia Wallace during a private 90-minute Strategy Intensive
                    designed for entrepreneurs, founders, executives, and business owners seeking
                    expert guidance, strategic clarity, and actionable next steps.
                  </p>
                  <p>
                    Whether you’re launching a business, scaling an existing company, exploring AI
                    integration, or refining your long-term strategy, this session is designed to
                    help you move forward with confidence.
                  </p>
                </div>
                <div className="mt-10 flex flex-wrap items-end gap-10">
                  <div>
                    <div className="eyebrow text-gold mb-2">Investment</div>
                    <div className="font-serif-display text-5xl tracking-tighter">
                      90 Minutes <span className="text-gold">•</span> $297
                    </div>
                  </div>
                </div>
                <div className="mt-10">
                  <a
                    href={STAN_STRATEGY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-cta-strategy"
                    className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte"
                  >
                    Book Your Strategy Intensive
                    <ArrowUpRight size={14} strokeWidth={1.6} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block eyebrow text-taupe mb-3 mt-6">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border-b border-taupe/40 py-3 text-matte focus:outline-none focus:border-gold font-serif-display text-xl"
      />
    </label>
  );
}

function ConfirmationCard({ onWriteAnother }) {
  return (
    <div
      data-testid="contact-confirmation"
      className="mt-12 bg-white rounded-3xl p-10 md:p-14 luxury-shadow border border-gold/30 relative overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gold/10 blur-3xl" aria-hidden />
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-gold/15 border border-gold/40 grid place-items-center mb-8 animate-fade-up">
          <CheckCircle2 size={26} strokeWidth={1.4} className="text-gold" />
        </div>
        <h3 className="font-serif-display text-4xl md:text-5xl tracking-tighter leading-[1.02] text-matte animate-fade-up" style={{ animationDelay: '80ms' }}>
          Thank you for reaching out.
        </h3>
        <p className="mt-6 text-matte/70 leading-[1.85] max-w-xl animate-fade-up" style={{ animationDelay: '160ms' }}>
          Your message has been received. We’ll review your inquiry and respond within
          1–2 business days.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: '240ms' }}>
          <button type="button" onClick={onWriteAnother} className="btn-ghost" data-testid="contact-write-another">
            Write another message
          </button>
        </div>
      </div>
    </div>
  );
}
